import { useState, useEffect } from 'react';
import { Project } from '../entities/Project';
import { Github, Code } from 'lucide-react';
import {
  SiKubernetes, SiGo, SiPython, SiDocker, SiTerraform,
  SiMysql, SiMongodb, SiPostgresql, SiRedis,
  SiPrometheus, SiGrafana, SiHelm, SiGooglecloud,
  SiGit, SiRust, SiCplusplus, SiJavascript, SiTypescript,
  SiNginx, SiAnsible, SiGithubactions,
  SiMqtt, SiInfluxdb, SiTensorflow, SiHomeassistant, SiTailscale, SiApachekafka,
  SiNodered,
} from 'react-icons/si';
import { FaAws, FaJava, FaNetworkWired } from 'react-icons/fa';
import { VscAzure } from 'react-icons/vsc';
import { TbApi } from 'react-icons/tb';

const TECH_ICON_MAP = {
  // Languages
  'go':           { icon: SiGo,           color: '#00ADD8' },
  'golang':       { icon: SiGo,           color: '#00ADD8' },
  'python':       { icon: SiPython,       color: '#3776AB' },
  'java':         { icon: FaJava,         color: '#ED8B00' },
  'rust':         { icon: SiRust,         color: '#DEA584' },
  'c/c++':        { icon: SiCplusplus,    color: '#00599C' },
  'javascript':   { icon: SiJavascript,   color: '#F7DF1E' },
  'typescript':   { icon: SiTypescript,   color: '#3178C6' },
  // Cloud
  'aws':          { icon: FaAws,          color: '#FF9900' },
  'azure':        { icon: VscAzure,       color: '#0078D4' },
  'gcp':          { icon: SiGooglecloud,  color: '#4285F4' },
  // Infra / Orchestration
  'kubernetes':   { icon: SiKubernetes,   color: '#326CE5' },
  'k3d':          { icon: SiKubernetes,   color: '#326CE5' },
  'hpa':          { icon: SiKubernetes,   color: '#326CE5' },
  'helm':         { icon: SiHelm,         color: '#0F1689' },
  'docker':       { icon: SiDocker,       color: '#2496ED' },
  'terraform':    { icon: SiTerraform,    color: '#844FBA' },
  'ansible':      { icon: SiAnsible,      color: '#EE0000' },
  'nginx':        { icon: SiNginx,        color: '#009639' },
  'github actions': { icon: SiGithubactions, color: '#2088FF' },
  // Databases
  'mysql':        { icon: SiMysql,        color: '#4479A1' },
  'mongodb':      { icon: SiMongodb,      color: '#47A248' },
  'postgresql':   { icon: SiPostgresql,   color: '#4169E1' },
  'redis':        { icon: SiRedis,        color: '#DC382D' },
  // Observability
  'prometheus':   { icon: SiPrometheus,   color: '#E6522C' },
  'grafana':      { icon: SiGrafana,      color: '#F46800' },
  // Networking / APIs
  'grpc':         { icon: TbApi,          color: '#4285F4' },
  'networking':   { icon: FaNetworkWired, color: '#22C55E' },
  // Dev tools
  'git':          { icon: SiGit,          color: '#F05032' },
  // IoT / ML
  'mqtt':         { icon: SiMqtt,         color: '#660066' },
  'influxdb':     { icon: SiInfluxdb,     color: '#22ADF6' },
  'tensorflow':   { icon: SiTensorflow,   color: '#FF6F00' },
  'yolov8':       { icon: SiTensorflow,   color: '#FF6F00' },
  'home assistant': { icon: SiHomeassistant, color: '#41BDF5' },
  'tailscale':    { icon: SiTailscale,    color: '#242424' },
  'kafka':        { icon: SiApachekafka,  color: '#E8E8E8' },
  'node-red':     { icon: SiNodered,      color: '#8F0000' },
};

function getTechIcon(name) {
  const key = name.toLowerCase()
    .replace(' innodb cluster', '')
    .replace(' sharded cluster', '')
    .replace(' sharded', '')
    .replace(' (cloudnativepg)', '')
    .replace(' sentinel', '')
    .replace(' streams', '')
    .replace(' operator', '')
    .trim();
  return TECH_ICON_MAP[key] ?? null;
}

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const data = await Project.filter({}, 'order_index', 50);
      setProjects(data);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section id="projects" className="scroll-mt-24 space-y-12">
        <div className="lg:hidden text-sm font-bold uppercase tracking-widest text-zinc-200 mb-8 sticky top-0 z-10 w-full bg-zinc-950/90 backdrop-blur pb-4 pt-10">
          Projects
        </div>
        <div className="flex flex-col gap-12">
          {[1, 2].map((i) => (
            <div key={i} className="animate-pulse flex flex-col sm:flex-row gap-6">
              <div className="sm:w-1/3 aspect-video bg-zinc-900 rounded"></div>
              <div className="sm:w-2/3 space-y-3">
                <div className="h-6 bg-zinc-900 rounded w-1/2"></div>
                <div className="h-4 bg-zinc-900 rounded w-full"></div>
                <div className="h-4 bg-zinc-900 rounded w-5/6"></div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section id="projects" className="scroll-mt-24 space-y-12">
      <div className="lg:hidden text-sm font-bold uppercase tracking-widest text-zinc-200 mb-8 sticky top-0 z-10 w-full bg-zinc-950/90 backdrop-blur pb-4 pt-10">
        Projects
      </div>

      {projects.length === 0 ? (
        <div className="text-zinc-500 py-12">
          Projects will appear here soon.
        </div>
      ) : (
        <div className="flex flex-col gap-16">
          {projects.map((project) => (
            <div key={project.id} className="group relative flex flex-col sm:flex-row gap-6 items-start">
              {/* Image Pane */}
              <div className="w-full sm:w-1/3 shrink-0 rounded border border-zinc-800/50 bg-zinc-900/50 overflow-hidden relative">
                <div className="aspect-video w-full bg-zinc-900">
                  {project.image_url ? (
                    <img
                      src={project.image_url}
                      alt={project.title}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-zinc-900">
                      <div className="w-8 h-8 rounded-sm border border-zinc-800 bg-zinc-950"></div>
                    </div>
                  )}
                </div>
              </div>

              {/* Content Pane */}
              <div className="w-full sm:w-2/3 flex flex-col">
                <h3 className="text-lg font-medium text-zinc-200 group-hover:text-zinc-50 transition-colors flex items-center gap-2">
                  {project.title}
                  {project.github_url && (
                    <a
                      href={project.github_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="opacity-0 group-hover:opacity-100 transition-all text-zinc-400 hover:text-zinc-200"
                      aria-label="GitHub repository"
                    >
                      <Github className="w-4 h-4" />
                    </a>
                  )}
                </h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                  {project.description}
                </p>

                {/* Tech Pills with Icons */}
                {project.technologies && project.technologies.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {project.technologies.map((tech, idx) => {
                      const match = getTechIcon(tech);
                      const IconComponent = match?.icon;
                      return (
                        <span
                          key={idx}
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-zinc-900/80 text-zinc-300 text-xs font-medium tracking-wide border border-zinc-800 hover:border-zinc-700 hover:text-zinc-100 transition-colors"
                        >
                          {IconComponent ? (
                            <IconComponent style={{ color: match.color, fontSize: '0.85rem' }} />
                          ) : (
                            <Code className="w-3 h-3 text-zinc-500" />
                          )}
                          {tech}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
